import useMonetization from '@/hooks/useMonetization';
import { Link } from "react-router-dom";
import { Check, ArrowRight, Store, AlertCircle } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import Photo from "@/components/site/Photo";
import ClosingCta from "@/components/site/ClosingCta";
import ProductLifestyleSplit from "@/components/site/ProductLifestyleSplit";
import PhoneMockup from "@/components/site/PhoneMockup";
import { APP_SHOT_BIZ, APP_SHOTS } from "@/lib/site";
import useSeo from "@/hooks/useSeo";
import {
  BUSINESS_PRICE, businessHowItWorks, businessPricingFeatures,
  businessCategories, businessEventExamples, businessHighlights, businessTrust, businessStory,
} from "@/data/business";

export default function Business() {
  const monetizationEnabled=useMonetization();
  useSeo({
    title: "Orrbbit Business | Connect With Your Local Community",
    description: "Create a verified Orrbbit Business Profile, host local events and connect with people nearby.",
    path: "/business",
  });
  return (
    <div data-testid="business-page">
      {/* Hero */}
      <ProductLifestyleSplit
        testid="business-hero"
        priority
        eyebrow={<><Store size={14} /> Orrbbit Business</>}
        eyebrowClass="bg-cobalt/10 text-cobalt"
        title="Turn nearby people into real local connections."
        body="Create a verified Business Profile, host local events and give people nearby a reason to discover your business."
        phoneSrc={APP_SHOT_BIZ}
        phoneAlt="Real Orrbbit app screenshot: Business home for a verified business, showing nearby people, active offers, upcoming events and performance"
        photo="pizza-restaurant"
      >
        <Link to="/business/register" data-testid="business-hero-register-btn" className="inline-flex items-center gap-2 rounded-full bg-cobalt px-7 py-3.5 font-semibold text-white shadow-soft transition-transform hover:scale-105">Create Business Account <ArrowRight size={18} /></Link>
        <Link to="/business/login" data-testid="business-login-btn" className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white px-7 py-3.5 font-semibold text-navy transition-colors hover:border-cobalt hover:text-cobalt">Business Login</Link>
      </ProductLifestyleSplit>

      <section className="container-x grid items-center gap-12 py-16 md:grid-cols-2">
        <div>
          <span className="text-sm font-semibold text-cobalt">Events, offers and your local community</span>
          <h2 className="mt-3 text-3xl font-bold text-navy">One place to run your presence nearby.</h2>
          <p className="mt-4 text-bodygrey">Create events, publish deals and manage your business profile from the same account on the website and in the app.</p>
          <p className="mt-4 text-bodygrey">Customers can show an offer QR code or redemption code. Your business checks and confirms it, so a discount is recorded when it is used.</p>
          <Link to="/business/dashboard" className="mt-6 inline-flex rounded-full bg-cobalt px-7 py-3 font-semibold text-white">Open business dashboard</Link>
          <p className="mt-4 text-xs text-bodygrey">Screens show the current app with fictional demo businesses and illustrative activity.</p>
        </div>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <figure><PhoneMockup src={APP_SHOTS.businessEvents} alt="Updated Business Events and Offers screen with discount code redemption" /><figcaption className="mt-3 text-center text-xs text-bodygrey">Events &amp; offers</figcaption></figure>
          <figure><PhoneMockup src={APP_SHOTS.businessProfile} alt="Updated Business Profile with separate Verified Business and Orrbbit Meet-up Spot badges" /><figcaption className="mt-3 text-center text-xs text-bodygrey">Business profile</figcaption></figure>
        </div>
      </section>

      {/* What you get */}
      <section className="container-x section-space">
        <Reveal className="max-w-2xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Bring your business into the local community.</h2>
          <p className="mt-3 text-bodygrey">Orrbbit Business gives local businesses a way to be discovered through real experiences, events and nearby opportunities.</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {businessHighlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <Reveal key={h.text} delay={i * 0.04}>
                <div className="flex h-full items-start gap-3 rounded-2xl border border-border bg-white p-5 shadow-soft">
                  <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-cobalt/10 text-cobalt"><Icon size={16} /></span>
                  <span className="text-sm font-medium text-navy">{h.text}</span>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* The Business story in real life */}
      <section className="border-t border-border bg-white" data-testid="business-story">
        <div className="container-x section-space">
          <Reveal className="max-w-2xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">From your door to their day.</h2>
            <p className="mt-3 text-bodygrey">A business joins Orrbbit, hosts something real, and nearby people show up. That's the loop.</p>
          </Reveal>
          <div className="mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 lg:grid lg:grid-cols-5 lg:overflow-visible lg:pb-0">
            {businessStory.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.05} className="w-60 flex-none snap-start lg:w-auto">
                <Photo slug={s.slug} ratio="aspect-[3/4]" rounded="rounded-2xl" className="shadow-soft" data-testid={`business-story-photo-${i}`} />
                <p className="mt-3 text-sm text-navy"><span className="font-semibold text-cobalt">{i + 1}.</span> {s.caption}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-border bg-white">
        <div className="container-x section-space">
          <Reveal className="max-w-2xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">How Orrbbit Business works</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {businessHowItWorks.filter(step=>monetizationEnabled||step.title!=="Review your plan").map((s, i) => (
              <Reveal key={s.title} delay={i * 0.04}>
                <div className="h-full rounded-2xl border border-border bg-white p-6 shadow-soft">
                  <div className="text-xs font-semibold text-cobalt">STEP {i + 1}</div>
                  <h3 className="mt-1 font-heading text-base font-semibold text-navy">{s.title}</h3>
                  <p className="mt-2 text-sm text-bodygrey">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-6 flex items-start gap-2 rounded-2xl border border-orange/20 bg-orange/5 p-5 text-sm text-navy" data-testid="business-verify-note">
            <AlertCircle size={18} className="mt-0.5 flex-none text-orange" />
            <span>Verification approval and an active trial or subscription are required for full Business features. Eligible new subscribers receive 7 days free, then A$9.99/month or the local store equivalent.</span>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-lightbg" data-testid="business-pricing-section">
        <div className="container-x section-space">
          <Reveal className="mx-auto max-w-xl">
            <div className="rounded-3xl border border-cobalt/30 bg-white p-8 shadow-card ring-1 ring-cobalt/10">
              <span className="text-xs font-semibold uppercase tracking-wider text-cobalt">Orrbbit Business</span>
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <span className="font-heading text-4xl font-bold text-navy">{BUSINESS_PRICE}</span>
                <span className="mb-1 text-sm font-semibold text-bodygrey">Full access</span>
              </div>
              <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {businessPricingFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-navy"><Check size={15} className="mt-0.5 flex-none text-cobalt" /> {f}</li>
                ))}
              </ul>
              <Link to="/business/register" data-testid="business-get-started-btn" className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-7 py-3.5 font-semibold text-white transition-transform hover:scale-[1.02]">Get Started as a Business</Link>
              <p className="mt-4 text-center text-xs text-bodygrey">Seven days free for eligible subscribers, then A$9.99/month (local store equivalent outside Australia) unless cancelled beforehand. Verification approval is required.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Event examples + categories */}
      <section className="container-x section-space">
        <Reveal className="max-w-2xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Host events your community discovers nearby</h2>
          <p className="mt-3 text-bodygrey">Business Hosted Events appear in nearby Event discovery in <span className="font-semibold text-cobalt">cobalt blue</span> — always tied to a real time, place and experience.</p>
          <p className="mt-2 text-[11px] italic text-bodygrey">Examples below are illustrative.</p>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {businessEventExamples.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.06}>
              <div className="h-full rounded-2xl border-l-4 border-cobalt bg-white p-6 shadow-soft" data-testid={`business-event-example-${i}`}>
                <span className="inline-flex rounded-full bg-cobalt/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-cobalt">Business Hosted · {e.tag}</span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{e.title}</h3>
                <p className="mt-1 text-sm text-bodygrey">{e.when}</p>
                <p className="mt-1 text-sm font-medium text-navy">{e.distance}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <p className="text-xs font-semibold uppercase tracking-wider text-bodygrey">Business event categories</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {businessCategories.map((c) => (
              <span key={c} className="inline-flex rounded-full border border-border bg-white px-3.5 py-1.5 text-sm font-medium text-navy shadow-soft">{c}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Trust */}
      <section className="border-t border-border bg-white">
        <div className="container-x section-space">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {businessTrust.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={t.title} delay={i * 0.06}>
                  <div className="h-full rounded-2xl border border-border bg-lightbg p-6">
                    <Icon className="text-cobalt" size={24} />
                    <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{t.title}</h3>
                    <p className="mt-2 text-sm text-bodygrey">{t.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-8">
            <p className="text-sm text-bodygrey">Reviews come from people who actually attended Orrbbit Business Events — only eligible confirmed attendees can review after an event has ended.</p>
          </Reveal>
        </div>
      </section>

      <section className="container-x py-12" id="verified-meetup-spots"><div className="rounded-3xl border border-orange/20 bg-orange/5 p-8"><h2 className="font-heading text-3xl font-bold text-navy">Become an Orrbbit Verified Meet-up Spot</h2><p className="mt-4 text-bodygrey">Give people a welcoming public place to meet. Use one Business account to promote your Business, become a Meet-up Spot, or both. Business verification and venue endorsement are reviewed separately.</p><ol className="mt-6 list-decimal pl-6 space-y-4 text-navy"><li><strong>Create your Business account.</strong> Add an accurate address, map pin, contact details and venue photos, then complete Business verification.</li><li><strong>Apply from your Business Profile.</strong> Choose Meet-up Spot or Both during signup, or apply later from Business Profile → Verified meet-up spot. Choose a clear shop photo, set weekly opening hours and timezone, and describe your public meeting area, accessibility and venue contact.</li><li><strong>Orrbbit reviews your application.</strong> Our team checks the venue details and may ask for more information. Track the decision and any requested updates in your account.</li><li><strong>Receive endorsement.</strong> Approved spots display their shop photo and a verification tick in the meet-up chooser while open, and an endorsement banner on their venue profile. Applying or subscribing alone does not grant endorsement.</li></ol><p className="mt-5 text-bodygrey">Keep your hours and venue details current. Orrbbit can suspend endorsement if requirements are no longer met. Endorsement reflects a venue review, not a guarantee of personal safety. No payment is taken when applying.</p><div className="mt-6 flex flex-wrap gap-5"><Link className="rounded-full bg-orange px-6 py-3 text-white font-semibold" to="/business/register?entry=meetup_spot">Become an Orrbbit Meet-up Spot</Link><Link className="py-3 font-semibold text-teal" to="/business/meetup-spot">Already registered? Apply or view status →</Link></div></div></section>
      <ClosingCta photo="workshop-seminar" tone="cobalt" title="Bring people together. Grow your local community." body="Host real experiences that nearby people discover, attend and remember." cta={{ to: "/business/register", label: "Create Business Account" }} testid="business-closing-cta" />
    </div>
  );
}

